import React, { useEffect, useState, useRef } from 'react';

import type { ApiClient } from '../api/client';
import type { JobWsTokenResponse } from '../types';
import {
  formatWebSocketDisconnectedMessage,
  formatWebSocketTokenFailureMessage
} from '../utils/errors';

export type JobStatus = 'queued' | 'processing' | 'done' | 'error' | 'canceled' | 'unknown';

export interface JobState {
  id: string;
  job_type: string;
  status: JobStatus;
  error?: string;
  created_at: string;
  done_at?: string;
  lock_at?: string;
}

interface BackgroundJobButtonProps {
  apiClient: ApiClient;
  appId: string;
  action: 'chown-fix';
  buttonLabel?: string;
  onComplete?: (job: JobState) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  className?: string;
  // Action-specific params
  params?: Record<string, unknown>;
}

/**
 * Reusable button component for enqueueing and tracking background jobs.
 *
 * Features:
 * - Enqueues a background job when clicked
 * - Uses websocket for real-time updates
 * - Shows loading state while job is running
 * - Calls onComplete/onError callbacks when job finishes
 *
 * Example usage:
 * ```tsx
 * <BackgroundJobButton
 *   apiClient={apiClient}
 *   appId={appId}
 *   action="chown-fix"
 *   params={{ target_uid: 10001 }}
 *   onComplete={(job) => console.log('Done!', job)}
 *   onError={(error) => setError(error)}
 * />
 * ```
 */
export function BackgroundJobButton({
  apiClient,
  appId,
  action,
  buttonLabel = 'Execute',
  onComplete,
  onError,
  disabled = false,
  className = '',
  params = {},
}: BackgroundJobButtonProps) {
  const [busy, setBusy] = useState(false);
  const [currentJob, setCurrentJob] = useState<JobState | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const currentJobId = currentJob?.id ?? null;

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  // Cleanup websocket on unmount or job completion
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // Set up websocket when currentJob changes
  useEffect(() => {
    if (!currentJobId) {
      // Cleanup when job is complete
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let disconnectedReported = false;
    let completed = false;
    let disposed = false;
    let tokenRequestId: string | null = null;

    const reportDisconnected = (event?: CloseEvent) => {
      if (disconnectedReported || completed || disposed) {
        return;
      }
      disconnectedReported = true;
      setBusy(false);
      setCurrentJob((job) => (job ? { ...job, status: 'unknown' } : job));
      onErrorRef.current?.(
        formatWebSocketDisconnectedMessage({
          context: 'Job updates disconnected',
          detail:
            'Live job status is paused; refresh the page after the connection is restored.',
          event,
          requestId: tokenRequestId
        })
      );
    };

    const setupWebSocket = () => {
      void (async () => {
        let wsToken = '';
        try {
          const response = await apiClient.post<JobWsTokenResponse>(
            `/api/jobs/${currentJobId}/ws-token`
          );
          wsToken = response.token;
          tokenRequestId = response.request_id ?? tokenRequestId;
        } catch (err) {
          const message = formatWebSocketTokenFailureMessage('Job updates disconnected', err);
          console.error(message, err);
          onErrorRef.current?.(message);
          setBusy(false);
          setCurrentJob((job) => (job ? { ...job, status: 'unknown' } : job));
          return;
        }
        if (disposed) {
          return;
        }
        const params = new URLSearchParams({ ws_token: wsToken });
        const wsUrl = `${wsProtocol}//${window.location.host}/api/jobs/${currentJobId}/ws?${params.toString()}`;
        let ws: WebSocket;
        try {
          ws = new WebSocket(wsUrl);
          wsRef.current = ws;
        } catch (err) {
          console.warn('Failed to create websocket:', err);
          reportDisconnected();
          return;
        }

        ws.onmessage = (event) => {
          if (disposed) {
            return;
          }
          try {
            const update: JobState = JSON.parse(event.data);
            setCurrentJob(update);

            if (update.status === 'done') {
              completed = true;
              setBusy(false);
              onCompleteRef.current?.(update);
              ws.close(1000);
              wsRef.current = null;
            } else if (update.status === 'error') {
              completed = true;
              setBusy(false);
              onErrorRef.current?.(update.error || 'Job failed');
              ws.close(1000);
              wsRef.current = null;
            } else if (update.status === 'canceled') {
              completed = true;
              setBusy(false);
              onErrorRef.current?.('Job was canceled');
              ws.close(1000);
              wsRef.current = null;
            }
          } catch (err) {
            console.error('Failed to parse websocket message:', err);
          }
        };

        ws.onerror = () => {
          console.warn('WebSocket connection failed');
          ws.close();
          reportDisconnected();
        };

        ws.onclose = (event) => {
          if (!completed && !event.wasClean) {
            reportDisconnected(event);
          }
        };
      })();
    };

    setupWebSocket();
    return () => {
      disposed = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [currentJobId, apiClient]);

  const handleClick = async () => {
    if (busy) return;

    setBusy(true);
    setCurrentJob(null);

    try {
      const endpoint = action === 'chown-fix'
        ? `/api/apps/${appId}/background-jobs/${action}`
        : `/api/apps/${appId}/background-jobs/${action}`;

      const response = await apiClient.post<{ job_id: string; status: string; message: string }>(
        endpoint,
        params
      );

      const status = response.status === 'pending' ? 'queued' : response.status;
      setCurrentJob({
        id: response.job_id,
        job_type: 'background',
        status: status as JobStatus,
        created_at: new Date().toISOString(),
      });
    } catch (err) {
      setBusy(false);
      const errorMsg = err instanceof Error ? err.message : 'Failed to enqueue job';
      onError?.(errorMsg);
    }
  };

  const getLabel = () => {
    if (currentJob) {
      switch (currentJob.status) {
        case 'queued':
          return 'Queued...';
        case 'processing':
          return 'Processing...';
        case 'done':
          return 'Done!';
        case 'error':
          return 'Failed';
        case 'canceled':
          return 'Canceled';
        default:
          return buttonLabel;
      }
    }
    return busy ? 'Starting...' : buttonLabel;
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || busy || (currentJob?.status === 'processing' || currentJob?.status === 'queued')}
      className={className}
    >
      {getLabel()}
    </button>
  );
}
